import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AG_GRID_LOCALE_BR } from '@ag-grid-community/locale';
import type { ColDef, GridReadyEvent, SelectionChangedEvent, CellValueChangedEvent, GridApi, GetRowIdParams } from 'ag-grid-community';
import { AllCommunityModule, ModuleRegistry, themeQuartz } from 'ag-grid-community';
import { PlusIcon, TrashIcon, MagnifyingGlassIcon, ArrowPathIcon, CheckIcon, XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { ProdutoGeral, ProdutoAgrupamento } from '../../api/agrupamento';
import { agrupamentoService } from '../../api/agrupamento';
import { useIsMobile } from '../../hooks/useIsMobile';

ModuleRegistry.registerModules([AllCommunityModule]);

// Tema compacto para mobile
const themeMobile = themeQuartz.withParams({
  rowHeight: 32,
  headerHeight: 32,
  fontSize: 12,
  cellHorizontalPadding: 6,
  spacing: 2,
});

// ──────────────────────────────────────────────────────────────
// Row types
// ──────────────────────────────────────────────────────────────
interface ProdutoRow extends ProdutoGeral {
  embalagemSelecionada: string;
  visivelCat: string;
}

interface ProdutoGrupoRow extends ProdutoAgrupamento {
  _descricao: string;
  _nomeEmbalagemOriginal: string;
  _visivelCatOriginal: string;
  _modificado: boolean;
}

// ──────────────────────────────────────────────────────────────
// Shared: badge visível catálogo
// ──────────────────────────────────────────────────────────────
const VisivelBadge: React.FC<{ ativo: boolean; modificado?: boolean; compact?: boolean; onClick: () => void }> = ({ ativo, modificado, compact, onClick }) => (
  <button
    onClick={onClick}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: compact ? '1px 6px' : '2px 10px',
      borderRadius: compact ? 8 : 12,
      fontSize: compact ? 10 : 12,
      fontWeight: 600,
      background: ativo ? '#dcfce7' : '#fee2e2',
      color: ativo ? '#15803d' : '#b91c1c',
      border: `1px solid ${ativo ? '#86efac' : '#fca5a5'}`,
      cursor: 'pointer',
      boxShadow: modificado ? '0 0 0 2px #fbbf24' : 'none',
      lineHeight: 1.3,
    }}
  >
    {compact ? (ativo ? 'S' : 'N') : (ativo ? 'Sim' : 'Não')}
  </button>
);

// ──────────────────────────────────────────────────────────────
// ABA: Produtos Disponíveis
// ──────────────────────────────────────────────────────────────
interface GridDisponiveisProps {
  produtos: ProdutoGeral[];
  carregando: boolean;
  onBuscar: (termo: string) => void;
  onVincular: (produtos: Array<{ codigo: string; embalagem: string; visivelCat: string }>) => Promise<void>;
  initialTermo?: string;
}

export const GridProdutosDisponiveis: React.FC<GridDisponiveisProps> = ({
  produtos, carregando, onBuscar, onVincular, initialTermo = '',
}) => {
  const isMobile = useIsMobile();
  const gridRef = useRef<AgGridReact<ProdutoRow>>(null);
  const [termoBusca, setTermoBusca] = useState(initialTermo);
  const [rowData, setRowData] = useState<ProdutoRow[]>([]);
  const [selecionados, setSelecionados] = useState<ProdutoRow[]>([]);
  const [vinculando, setVinculando] = useState(false);

  useEffect(() => {
    setRowData(produtos.map(p => ({ ...p, embalagemSelecionada: p.embalagem ?? '', visivelCat: 'S' })));
  }, [produtos]);

  const handleToggleVisivelCat = useCallback((codigo: string) => {
    setRowData(prev => prev.map(r =>
      r.codigo !== codigo ? r : { ...r, visivelCat: r.visivelCat === 'S' ? 'N' : 'S' }
    ));
  }, []);

  const columnDefs = useMemo<ColDef<ProdutoRow>[]>(() => {
    const cols: ColDef<ProdutoRow>[] = [
      {
        checkboxSelection: true,
        headerCheckboxSelection: true,
        width: isMobile ? 36 : 48, minWidth: isMobile ? 36 : 48, maxWidth: isMobile ? 36 : 48,
        suppressSizeToFit: true,
        pinned: 'left',
      },
      { field: 'codigo', headerName: 'Código', width: isMobile ? 75 : 90, filter: 'agTextColumnFilter', pinned: 'left' },
      { field: 'descricao', headerName: 'Descrição', flex: 2, filter: 'agTextColumnFilter', minWidth: isMobile ? 100 : 150 },
    ];

    if (!isMobile) {
      cols.push(
        { field: 'referencia', headerName: 'Referência', width: 120, filter: 'agTextColumnFilter' },
        { field: 'codigoFabrica', headerName: 'Cód. Fábrica', width: 130, filter: 'agTextColumnFilter' },
      );
    }

    cols.push(
      { field: 'embalagem', headerName: 'Emb.', width: isMobile ? 60 : 100, filter: 'agTextColumnFilter' },
      { field: 'qtdeEmbalagem', headerName: 'Qtde', width: isMobile ? 55 : 95, filter: 'agNumberColumnFilter', valueFormatter: (p) => p.value ? String(p.value) : '' },
      {
        field: 'embalagemSelecionada', headerName: isMobile ? 'Label' : 'Label Embalagem', width: isMobile ? 90 : 155,
        editable: true, cellStyle: { background: '#f0fdf4', cursor: 'text' },
        cellRenderer: (params: { value: string }) =>
          params.value
            ? <span className="font-medium text-emerald-700 dark:text-emerald-400">{params.value}</span>
            : <span className="text-zinc-400 italic text-[10px]">{isMobile ? '—' : 'clique para editar'}</span>,
      },
      {
        field: 'visivelCat', headerName: isMobile ? 'Vis.' : 'Visível Catálogo', width: isMobile ? 55 : 130,
        cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        cellRenderer: (params: { value: string; data?: ProdutoRow }) => (
          <VisivelBadge ativo={(params.value ?? 'S') === 'S'} onClick={() => handleToggleVisivelCat(params.data?.codigo ?? '')} compact={isMobile} />
        ),
      },
    );

    return cols;
  }, [isMobile, handleToggleVisivelCat]);

  const defaultColDef = useMemo<ColDef>(() => ({ sortable: true, resizable: true, suppressMovable: false }), []);
  const getRowId = useCallback((params: GetRowIdParams<ProdutoRow>) => params.data.codigo, []);

  const onSelectionChanged = useCallback((e: SelectionChangedEvent<ProdutoRow>) => {
    setSelecionados(e.api.getSelectedRows());
  }, []);

  const onCellValueChanged = useCallback((e: CellValueChangedEvent<ProdutoRow>) => {
    if (e.colDef.field === 'embalagemSelecionada') {
      setRowData(prev => prev.map(r =>
        r.codigo !== e.data?.codigo ? r : { ...r, embalagemSelecionada: e.newValue ?? '' }
      ));
    }
  }, []);

  const handleBuscar = useCallback(() => { onBuscar(termoBusca); }, [termoBusca, onBuscar]);

  const autoSearchRef = useRef(false);
  useEffect(() => {
    if (autoSearchRef.current) return;
    autoSearchRef.current = true;
    if (initialTermo.trim().length >= 2) onBuscar(initialTermo);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleVincular = useCallback(async () => {
    if (selecionados.length === 0) return;
    setVinculando(true);
    try {
      const codigos = new Set(selecionados.map(s => s.codigo));
      const produtosAtualizados = rowData.filter(r => codigos.has(r.codigo));
      await onVincular(produtosAtualizados.map(p => ({
        codigo: p.codigo, embalagem: p.embalagemSelecionada, visivelCat: p.visivelCat,
      })));
      gridRef.current?.api?.deselectAll();
      setSelecionados([]);
    } finally {
      setVinculando(false);
    }
  }, [selecionados, rowData, onVincular]);

  return (
    <div className="flex flex-col h-full gap-2 md:gap-3">
      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-0">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <input
            type="text"
            value={termoBusca}
            onChange={e => setTermoBusca(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleBuscar()}
            placeholder="Buscar por código ou descrição…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-zinc-300 dark:border-zinc-600 rounded-lg
                       bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100
                       placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <button
          onClick={handleBuscar}
          disabled={carregando}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg
                     bg-zinc-100 dark:bg-zinc-700 hover:bg-zinc-200 dark:hover:bg-zinc-600
                     text-zinc-700 dark:text-zinc-200 transition-colors disabled:opacity-50"
        >
          {carregando
            ? <ArrowPathIcon className="w-4 h-4 animate-spin" />
            : <MagnifyingGlassIcon className="w-4 h-4" />}
          <span className="hidden sm:inline">Buscar</span>
        </button>
      </div>

      {selecionados.length > 0 && (
        <button
          onClick={handleVincular}
          disabled={vinculando}
          className="flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg
                     bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-70 w-full sm:w-auto"
        >
          {vinculando
            ? <ArrowPathIcon className="w-4 h-4 animate-spin" />
            : <PlusIcon className="w-4 h-4" />}
          Vincular {selecionados.length} produto{selecionados.length > 1 ? 's' : ''}
        </button>
      )}

      {/* Hint — only desktop */}
      <p className="text-xs text-zinc-400 dark:text-zinc-500 hidden md:block">
        Selecione produtos e edite <strong>Label Embalagem</strong> e <strong>Visível Catálogo</strong> antes de vincular.
      </p>

      {/* Grid */}
      <div
        className="flex-1 rounded-lg overflow-hidden border border-zinc-200 dark:border-zinc-700"
        style={{ minHeight: isMobile ? 280 : 380 }}
      >
        <AgGridReact<ProdutoRow>
          ref={gridRef}
          theme={isMobile ? themeMobile : themeQuartz}
          localeText={AG_GRID_LOCALE_BR}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowId={getRowId}
          rowSelection={{ mode: 'multiRow', enableClickSelection: false }}
          onSelectionChanged={onSelectionChanged}
          onCellValueChanged={onCellValueChanged}
          animateRows
          pagination
          paginationPageSize={isMobile ? 25 : 50}
          paginationPageSizeSelector={isMobile ? [25, 50] : [25, 50, 100]}
          overlayLoadingTemplate='<span class="text-sm text-zinc-500">Carregando produtos…</span>'
          overlayNoRowsTemplate='<span class="text-sm text-zinc-400">Nenhum produto encontrado. Use a busca acima.</span>'
          loading={carregando}
        />
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────
// ABA: Produtos no Grupo
// ──────────────────────────────────────────────────────────────
interface GridGrupoProps {
  grupoId: number;
  produtos: ProdutoAgrupamento[];
  carregando: boolean;
  onRemover: (produtoCodigo: string) => Promise<void>;
  onAlterado?: () => void;
}

export const GridProdutosGrupo: React.FC<GridGrupoProps> = ({
  grupoId, produtos, carregando, onRemover, onAlterado,
}) => {
  const isMobile = useIsMobile();
  const [rowData, setRowData] = useState<ProdutoGrupoRow[]>([]);
  const [removendo, setRemovendo] = useState<Set<string>>(new Set());
  const [salvando, setSalvando] = useState(false);
  const gridApiRef = useRef<GridApi | null>(null);
  const [quickFilter, setQuickFilter] = useState('');

  useEffect(() => {
    setRowData(produtos.map(p => ({
      ...p,
      _descricao: p.descricaoProduto ?? p.produto?.descricao ?? '—',
      _nomeEmbalagemOriginal: p.nomeEmbalagem ?? '',
      _visivelCatOriginal: p.visivelCat ?? 'S',
      _modificado: false,
    })));
  }, [produtos]);

  const modificados = useMemo(() => rowData.filter(r => r._modificado), [rowData]);

  const handleToggleVisivelCat = useCallback((codigo: string) => {
    setRowData(prev => prev.map(r => {
      if (r.produtoCodigo !== codigo) return r;
      const novo = (r.visivelCat ?? 'S') === 'S' ? 'N' : 'S';
      return { ...r, visivelCat: novo, _modificado: novo !== r._visivelCatOriginal || (r.nomeEmbalagem ?? '') !== r._nomeEmbalagemOriginal };
    }));
  }, []);

  const onCellValueChanged = useCallback((e: CellValueChangedEvent<ProdutoGrupoRow>) => {
    if (e.colDef.field === 'nomeEmbalagem') {
      const newVal = e.newValue ?? '';
      setRowData(prev => prev.map(r => {
        if (r.produtoCodigo !== e.data?.produtoCodigo) return r;
        return { ...r, nomeEmbalagem: newVal, _modificado: newVal !== r._nomeEmbalagemOriginal || (r.visivelCat ?? 'S') !== r._visivelCatOriginal };
      }));
    }
  }, []);

  const handleSalvar = useCallback(async () => {
    if (modificados.length === 0) return;
    setSalvando(true);
    try {
      await agrupamentoService.salvarManutencaoLote(
        modificados.map(r => ({ codigo: r.produtoCodigo, grupoIdAnterior: grupoId, grupoIdNovo: grupoId, nomeEmbalagem: r.nomeEmbalagem, visivelCat: r.visivelCat ?? 'S' }))
      );
      setRowData(prev => prev.map(r =>
        r._modificado ? { ...r, _nomeEmbalagemOriginal: r.nomeEmbalagem ?? '', _visivelCatOriginal: r.visivelCat ?? 'S', _modificado: false } : r
      ));
      onAlterado?.();
    } finally { setSalvando(false); }
  }, [modificados, grupoId, onAlterado]);

  const handleDescartar = useCallback(() => {
    setRowData(prev => prev.map(r =>
      r._modificado ? { ...r, nomeEmbalagem: r._nomeEmbalagemOriginal, visivelCat: r._visivelCatOriginal, _modificado: false } : r
    ));
  }, []);

  const handleRemover = useCallback(async (codigo: string) => {
    setRemovendo(prev => new Set(prev).add(codigo));
    try { await onRemover(codigo); }
    finally { setRemovendo(prev => { const s = new Set(prev); s.delete(codigo); return s; }); }
  }, [onRemover]);

  const columnDefs = useMemo<ColDef<ProdutoGrupoRow>[]>(() => {
    const cols: ColDef<ProdutoGrupoRow>[] = [
      { field: 'produtoCodigo', headerName: 'Código', width: isMobile ? 75 : 110, filter: 'agTextColumnFilter', pinned: 'left' },
      { field: '_descricao', headerName: 'Descrição', flex: 2, filter: 'agTextColumnFilter', minWidth: isMobile ? 100 : 150 },
    ];

    if (!isMobile) {
      cols.push(
        { field: 'referencia', headerName: 'Referência', width: 120, filter: 'agTextColumnFilter' },
        { field: 'codigoFabrica', headerName: 'Cód. Fábrica', width: 130, filter: 'agTextColumnFilter' },
      );
    }

    cols.push(
      { field: 'embalagem', headerName: 'Emb.', width: isMobile ? 60 : 100, filter: 'agTextColumnFilter' },
      { field: 'qtdeEmbalagem', headerName: 'Qtde', width: isMobile ? 55 : 95, filter: 'agNumberColumnFilter', valueFormatter: (p) => p.value ? String(p.value) : '' },
      {
        field: 'nomeEmbalagem', headerName: isMobile ? 'Label' : 'Label Embalagem', width: isMobile ? 90 : 160,
        editable: true,
        cellStyle: (params) => ({
          background: (params.data?._modificado && (params.data.nomeEmbalagem ?? '') !== params.data._nomeEmbalagemOriginal) ? '#bbf7d0' : '#f0fdf4',
          cursor: 'text',
        }),
        cellRenderer: (params: { value: string; data?: ProdutoGrupoRow }) =>
          params.value
            ? <span className="font-medium text-emerald-700 dark:text-emerald-400">{params.value}</span>
            : <span className="text-zinc-400 italic text-[10px]">{isMobile ? '—' : 'clique para editar'}</span>,
      },
      {
        field: 'visivelCat', headerName: isMobile ? 'Vis.' : 'Visível Catálogo', width: isMobile ? 55 : 130,
        cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        cellRenderer: (params: { value: string; data?: ProdutoGrupoRow }) => {
          const ativo = (params.value ?? 'S') === 'S';
          const modificado = ativo !== (params.data?._visivelCatOriginal === 'S');
          return <VisivelBadge ativo={ativo} modificado={modificado} onClick={() => handleToggleVisivelCat(params.data?.produtoCodigo ?? '')} compact={isMobile} />;
        },
      },
    );

    if (!isMobile) {
      cols.push({
        field: 'dataInclusao', headerName: 'Incluído em', width: 120,
        valueFormatter: (p) => p.value ? new Date(p.value).toLocaleDateString('pt-BR') : '—',
      });
    }

    cols.push({
      headerName: '', width: isMobile ? 38 : 52, suppressSizeToFit: true,
      cellRenderer: (params: { data?: ProdutoGrupoRow }) => {
        const codigo = params.data?.produtoCodigo;
        if (!codigo) return null;
        const isRemoving = removendo.has(codigo);
        return (
          <button onClick={() => handleRemover(codigo)} disabled={isRemoving} title="Remover do grupo"
            className="flex items-center justify-center w-7 h-7 rounded-md text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/30 transition-colors disabled:opacity-40">
            {isRemoving ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <TrashIcon className="w-4 h-4" />}
          </button>
        );
      },
    });

    return cols;
  }, [isMobile, removendo, handleRemover, handleToggleVisivelCat]);

  const defaultColDef = useMemo<ColDef>(() => ({ sortable: true, resizable: true }), []);
  const getRowId = useCallback((params: GetRowIdParams<ProdutoGrupoRow>) => params.data.produtoCodigo, []);
  const getRowStyle = useCallback((params: { data?: ProdutoGrupoRow }) =>
    params.data?._modificado ? { backgroundColor: 'rgba(251,191,36,0.07)' } : undefined, []);

  const onGridReady = useCallback((e: GridReadyEvent) => { gridApiRef.current = e.api; }, []);

  const handleQuickFilter = (v: string) => {
    setQuickFilter(v);
    gridApiRef.current?.setGridOption('quickFilterText', v);
  };

  return (
    <div className="flex flex-col h-full gap-2 md:gap-3">
      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-0 max-w-xs">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <input
            type="text"
            value={quickFilter}
            onChange={e => handleQuickFilter(e.target.value)}
            placeholder="Filtrar esta lista…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-zinc-300 dark:border-zinc-600 rounded-lg
                       bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100
                       placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <span className="text-xs md:text-sm text-zinc-500 dark:text-zinc-400">
          {produtos.length} produto{produtos.length !== 1 ? 's' : ''}
        </span>
        <span className="ml-auto text-xs text-zinc-400 dark:text-zinc-500 hidden lg:block">
          Clique em <strong>Label Embalagem</strong> para editar · <strong>Visível</strong> para ativar/desativar
        </span>
      </div>

      {/* Grid */}
      <div
        className="flex-1 rounded-lg overflow-hidden border border-zinc-200 dark:border-zinc-700"
        style={{ minHeight: isMobile ? 250 : 300 }}
      >
        <AgGridReact<ProdutoGrupoRow>
          onGridReady={onGridReady}
          theme={isMobile ? themeMobile : themeQuartz}
          localeText={AG_GRID_LOCALE_BR}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowId={getRowId}
          getRowStyle={getRowStyle}
          onCellValueChanged={onCellValueChanged}
          animateRows
          pagination
          paginationPageSize={isMobile ? 25 : 50}
          paginationPageSizeSelector={isMobile ? [25, 50] : [25, 50, 100]}
          overlayNoRowsTemplate='<span class="text-sm text-zinc-400">Nenhum produto vinculado a este grupo.</span>'
          loading={carregando}
        />
      </div>

      {/* Footer: alterações pendentes */}
      {modificados.length > 0 && (
        <div className="flex-none flex items-center gap-2 md:gap-3 px-2 md:px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg flex-wrap">
          <ExclamationTriangleIcon className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <span className="text-[11px] md:text-xs text-amber-700 dark:text-amber-400 flex-1 min-w-0">
            {modificados.length} alteração{modificados.length !== 1 ? 'ões' : ''}
          </span>
          <button onClick={handleDescartar}
            className="flex items-center gap-1 px-2 md:px-2.5 py-1.5 text-[11px] md:text-xs font-medium rounded-md
                       text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors">
            <XMarkIcon className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Descartar</span>
          </button>
          <button onClick={handleSalvar} disabled={salvando}
            className="flex items-center gap-1 px-2 md:px-3 py-1.5 text-[11px] md:text-xs font-medium rounded-md
                       bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50">
            {salvando ? <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" /> : <CheckIcon className="w-3.5 h-3.5" />}
            {salvando ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      )}
    </div>
  );
};
